import React from 'react';
import { YSheet } from '../../../ui/sheet/YSheet';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YSelect } from '../../../ui/YSelect';
import { weatherService } from '../../map/services/weatherService';
import { toolkitService } from '../services/toolkitService';
import {
  WEATHER_MAINS,
  buildPlantingEventPayload,
  emptyPlantingWeather,
  localTodayIso,
  markWeatherManual,
  resolvePlanCoords,
  shouldAutoFetchWeather,
  weatherFromCurrent,
} from './plantingMarkPlantedUtils';

function weatherHint({ plantedAt, coords, weatherLoading, weatherError, source }) {
  if (weatherLoading) return 'Looking up today’s weather for this plan…';
  if (weatherError) return 'Weather lookup failed. Fill it in or save without it.';
  if (!weatherService.isWeatherConfigured()) {
    return 'Weather is not configured. Fill it in, or skip it.';
  }
  if (!shouldAutoFetchWeather({ plantedAt, coords })) {
    if (!coords) return 'No location on this plan. Fill weather yourself, or skip it.';
    return 'Date is not today. Fill weather yourself, or skip it.';
  }
  if (source === 'auto') return 'Filled from today’s weather. You can edit it.';
  return 'Today’s weather for this plan. You can edit it.';
}

export function PlantingMarkPlantedDialog({
  group,
  show = false,
  onHide,
  onSaved,
}) {
  const [plantedAt, setPlantedAt] = React.useState(localTodayIso());
  const [notes, setNotes] = React.useState('');
  const [weather, setWeather] = React.useState(emptyPlantingWeather());
  const [weatherLoading, setWeatherLoading] = React.useState(false);
  const [weatherError, setWeatherError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const fetchGen = React.useRef(0);
  const weatherTouchedRef = React.useRef(false);

  const coords = React.useMemo(() => resolvePlanCoords(group), [group]);
  const planName = group?.name || 'this plan';

  const resetForm = React.useCallback(() => {
    weatherTouchedRef.current = false;
    setPlantedAt(localTodayIso());
    setNotes('');
    setWeather(emptyPlantingWeather());
    setWeatherLoading(false);
    setWeatherError('');
    setSaving(false);
    setSaveError('');
  }, []);

  React.useEffect(() => {
    if (show) resetForm();
  }, [show, group?.id, resetForm]);

  React.useEffect(() => {
    if (!show) return undefined;
    const gen = ++fetchGen.current;
    if (!shouldAutoFetchWeather({ plantedAt, coords }) || !weatherService.isWeatherConfigured()) {
      setWeatherLoading(false);
      return undefined;
    }
    let cancelled = false;
    setWeatherLoading(true);
    setWeatherError('');
    weatherService.getCurrentWeather(coords.latitude, coords.longitude)
      .then((current) => {
        if (cancelled || gen !== fetchGen.current || weatherTouchedRef.current) return;
        setWeather(weatherFromCurrent(current));
        setWeatherError('');
      })
      .catch(() => {
        if (cancelled || gen !== fetchGen.current) return;
        setWeather(emptyPlantingWeather());
        setWeatherError('lookup-failed');
      })
      .finally(() => {
        if (cancelled || gen !== fetchGen.current) return;
        setWeatherLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, plantedAt, coords]);

  const handleDateChange = (value) => {
    fetchGen.current += 1;
    weatherTouchedRef.current = false;
    setPlantedAt(value);
    setWeather(emptyPlantingWeather());
    setWeatherError('');
    setWeatherLoading(false);
  };

  const handleWeatherField = (key, value) => {
    weatherTouchedRef.current = true;
    setWeather((prev) => markWeatherManual({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!group?.id || !plantedAt || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const created = await toolkitService.createPlantingEvent(
        await buildPlantingEventPayload(group, {
          plantedAt,
          notes,
          weather,
          loadPlan: toolkitService.getPlanPreview,
        })
      );
      onSaved?.(created);
      onHide?.();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Could not log this planting.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <YSheet
      show={Boolean(show && group)}
      onHide={onHide}
      kicker="PLANT"
      title="Mark as planted"
      testId="plant-mark-planted-dialog"
      error={saveError}
      footer={(
        <>
          <YButton variant="secondary" type="button" onClick={onHide} disabled={saving}>
            Cancel
          </YButton>
          <YButton
            variant="primary"
            type="submit"
            form="plant-mark-planted-form"
            data-testid="plant-mark-planted-submit"
            disabled={saving || !plantedAt}
          >
            {saving ? 'Saving…' : 'Log planting'}
          </YButton>
        </>
      )}
    >
      <form
        id="plant-mark-planted-form"
        className="plant-mark-planted"
        onSubmit={handleSubmit}
      >
        <p className="plant-mark-planted__lede">
          Log a planting for <strong>{planName}</strong>. The plan stays editable.
        </p>
        <div className="y-dialog__row">
          <div>
            <label className="y-label--dialog" htmlFor="plant-mark-planted-date">
              Planted on
            </label>
            <YInput
              id="plant-mark-planted-date"
              data-testid="plant-mark-planted-date"
              type="date"
              value={plantedAt}
              onChange={(event) => handleDateChange(event.target.value)}
              required
            />
          </div>
        </div>
        <div className="y-dialog__row">
          <div>
            <label className="y-label--dialog" htmlFor="plant-mark-planted-notes">
              Notes
            </label>
            <YInput
              id="plant-mark-planted-notes"
              data-testid="plant-mark-planted-notes"
              type="textarea"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Anything worth remembering…"
            />
          </div>
        </div>
        <p className="plant-mark-planted__hint" data-testid="plant-mark-planted-weather-hint">
          {weatherHint({
            plantedAt,
            coords,
            weatherLoading,
            weatherError,
            source: weather.source,
          })}
        </p>
        <div className="y-dialog__row y-dialog__row--2">
          <div>
            <label className="y-label--dialog" htmlFor="plant-mark-planted-main">
              Condition
            </label>
            <YSelect
              id="plant-mark-planted-main"
              data-testid="plant-mark-planted-main"
              value={weather.main || ''}
              onChange={(event) => handleWeatherField('main', event.target.value)}
            >
              <option value="">Skip</option>
              {WEATHER_MAINS.map((main) => (
                <option key={main} value={main}>{main}</option>
              ))}
            </YSelect>
          </div>
          <div>
            <label className="y-label--dialog" htmlFor="plant-mark-planted-temp">
              Temp (°F)
            </label>
            <YInput
              id="plant-mark-planted-temp"
              data-testid="plant-mark-planted-temp"
              type="number"
              value={weather.temperature}
              onChange={(event) => handleWeatherField('temperature', event.target.value)}
              placeholder="72"
            />
          </div>
        </div>
        <div className="y-dialog__row">
          <div>
            <label className="y-label--dialog" htmlFor="plant-mark-planted-description">
              Description
            </label>
            <YInput
              id="plant-mark-planted-description"
              data-testid="plant-mark-planted-description"
              value={weather.description}
              onChange={(event) => handleWeatherField('description', event.target.value)}
              placeholder="scattered clouds"
            />
          </div>
        </div>
        <div className="y-dialog__row y-dialog__row--2">
          <div>
            <label className="y-label--dialog" htmlFor="plant-mark-planted-wind">
              Wind (mph)
            </label>
            <YInput
              id="plant-mark-planted-wind"
              data-testid="plant-mark-planted-wind"
              type="number"
              value={weather.wind_speed}
              onChange={(event) => handleWeatherField('wind_speed', event.target.value)}
              placeholder="8"
            />
          </div>
          <div>
            <label className="y-label--dialog" htmlFor="plant-mark-planted-precip">
              Precip (mm)
            </label>
            <YInput
              id="plant-mark-planted-precip"
              data-testid="plant-mark-planted-precip"
              type="number"
              step="0.1"
              value={weather.precip_mm}
              onChange={(event) => handleWeatherField('precip_mm', event.target.value)}
              placeholder="0"
            />
          </div>
        </div>
      </form>
    </YSheet>
  );
}
